import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import {
  formatError,
  formatSuccess,
} from "../../../core/data/redux/service/AuthService";
import axios from "axios";
import { API_URL } from "../../../server";
import { MD5 } from "crypto-js";

const STEPS = [
  {
    emoji: "📱",
    title: "Enter your number",
    description: "Use the mobile or email linked to your account.",
  },
  {
    emoji: "🔑",
    title: "Set a new password",
    description: "Choose a strong password between 8 and 15 characters.",
  },
  {
    emoji: "✅",
    title: "Verify with OTP",
    description: "Confirm the one-time code sent to your device.",
  },
] as const;

const FLOATING_BADGES = [
  { emoji: "🔒", label: "Secure reset", className: "login-page__float-badge--1" },
  { emoji: "⚡", label: "Quick process", className: "login-page__float-badge--2" },
  { emoji: "🛡️", label: "Account safe", className: "login-page__float-badge--3" },
] as const;

const ForgotPassword2 = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [data, setData] = useState({
    mobileNumber: "",
    newPwd: "",
    confirmPwd: "",
  });

  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [error, setError] = useState({
    mobileNumber: "",
    newPwd: "",
    confirmPwd: "",
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value);
  };

  const sendOtp = async () => {
    if (validateData()) {
      const formData = new FormData();
      formData.append("mobileNumber", data.mobileNumber);
      try {
        const res = await axios.post(
          `${API_URL}/api/auth/authenticateUser`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (res) {
          setIsOtpSent(true);
          formatSuccess(res.data?.message);
        }
      } catch (error: any) {
        formatError(error.response.data?.message);
      }
    }
  };

  const changepwd = async () => {
    if (validateData()) {
      const encKey = await MD5(data.newPwd).toString();
      const formData = new FormData();
      formData.append("mobileNumber", data.mobileNumber);
      formData.append("newPwd", encKey);
      formData.append("otp", otp);

      try {
        const res = await axios.put(
          `${API_URL}/api/user/forgot-pwd`,
          formData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (res) {
          navigate(routes.login);
          formatSuccess(res.data);
        }
      } catch (error: any) {
        formatError(error.response.data);
      }
    }
  };

  const validateData = () => {
    let isValid = true;
    const newErrors = { mobileNumber: "", newPwd: "", confirmPwd: "" };

    if (!data.mobileNumber) {
      newErrors.mobileNumber = "Mobile number is required.";
      isValid = false;
    }
    if (data.newPwd.length < 8 || data.newPwd.length > 15) {
      newErrors.newPwd = "Password must be between 8 to 15 characters long.";
      isValid = false;
    }
    if (data.newPwd !== data.confirmPwd) {
      newErrors.confirmPwd = "Passwords do not match.";
      isValid = false;
    }

    setError(newErrors);
    return isValid;
  };

  return (
    <div className="login-page">
      <div className="login-page__decor login-page__decor--1" aria-hidden="true">🔐</div>
      <div className="login-page__decor login-page__decor--2" aria-hidden="true">✨</div>
      <div className="login-page__decor login-page__decor--3" aria-hidden="true">🔑</div>

      <section className="login-page__hero">
        <div className="login-page__hero-content">
          <div className="login-page__hero-intro">
            <div className="login-page__badge-pill">
              <span aria-hidden="true">🔐</span> Secure password recovery
            </div>

            <h1 className="login-page__headline">
              Reset your password <span>quickly</span> and securely
            </h1>

            <p className="login-page__tagline">
              Regain access to TaskLync in a few simple steps — verify your
              identity and set a new password.
            </p>
          </div>

          <div className="login-page__hero-bottom">
            <div className="login-page__features">
              {STEPS.map((step) => (
                <div key={step.title} className="login-page__feature">
                  <div className="login-page__feature-icon" aria-hidden="true">
                    {step.emoji}
                  </div>
                  <div className="login-page__feature-text">
                    <h6>{step.title}</h6>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="login-page__hero-visual" aria-hidden="true">
              <div className="login-page__hero-illustration">
                <ImageWithBasePath
                  src="assets/img/bg/login-hero-reset.svg"
                  alt="Password reset illustration"
                  className="login-page__hero-illustration-img"
                />
              </div>
              {FLOATING_BADGES.map((badge) => (
                <div key={badge.label} className={`login-page__float-badge ${badge.className}`}>
                  <span className="login-page__float-badge-emoji">{badge.emoji}</span>
                  <span className="login-page__float-badge-label">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="login-page__hero-footer mb-0">Copyright © 2025 - HKCL</p>
      </section>

      <aside className="login-page__panel">
        <div className="login-page__panel-inner login-page__panel-inner--compact">
          <div className="login-page__panel-logo">
            <img src="assets/tl-full.png" alt="TaskLync" />
          </div>

          <div className="login-page__panel-header text-center">
            <h2 className="mb-2">
              Forgot Password? <span className="login-page__welcome-emoji" aria-hidden="true">🔑</span>
            </h2>
            <p className="mb-0">
              {isOtpSent
                ? "Enter the OTP sent to your device"
                : "Enter your details to reset your password"}
            </p>
          </div>

          <form className="login-page__form" onSubmit={(e) => e.preventDefault()}>
            {!isOtpSent && (
              <>
                <div className="mb-3">
                  <label className="form-label">Email / Mobile Number</label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="mobileNumber"
                      value={data.mobileNumber}
                      onChange={handleChange}
                      placeholder="Email / Mobile Number"
                      className="form-control border-end-0"
                      required
                    />
                    <span className="input-group-text border-start-0">
                      <i className="ti ti-mail" />
                    </span>
                  </div>
                  {error.mobileNumber && (
                    <p className="text-danger small mb-0 mt-1">{error.mobileNumber}</p>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <div className="input-group">
                    <input
                      type="password"
                      name="newPwd"
                      value={data.newPwd}
                      onChange={handleChange}
                      placeholder="New password"
                      className="form-control border-end-0"
                      required
                    />
                    <span className="input-group-text border-start-0">
                      <i className="ti ti-lock" />
                    </span>
                  </div>
                  {error.newPwd && (
                    <p className="text-danger small mb-0 mt-1">{error.newPwd}</p>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Confirm Password</label>
                  <div className="input-group">
                    <input
                      type="password"
                      name="confirmPwd"
                      value={data.confirmPwd}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      className="form-control border-end-0"
                      required
                    />
                    <span className="input-group-text border-start-0">
                      <i className="ti ti-lock" />
                    </span>
                  </div>
                  {error.confirmPwd && (
                    <p className="text-danger small mb-0 mt-1">{error.confirmPwd}</p>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-primary w-100 login-page__signin-btn"
                  onClick={sendOtp}
                >
                  Send OTP <span aria-hidden="true">→</span>
                </button>
              </>
            )}

            {isOtpSent && (
              <>
                <div className="mb-3">
                  <label className="form-label">Enter OTP</label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="otp"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="One-time password"
                      className="form-control border-end-0"
                      required
                    />
                    <span className="input-group-text border-start-0">
                      <i className="ti ti-key" />
                    </span>
                  </div>
                </div>

                <div className="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill login-page__signin-btn"
                    onClick={changepwd}
                  >
                    Change Password
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger flex-fill"
                    onClick={sendOtp}
                  >
                    Resend OTP
                  </button>
                </div>
              </>
            )}

            <div className="text-center mt-3">
              <p className="mb-0 text-muted">
                Return to{" "}
                <Link to={routes.login} className="hover-a">
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>
      </aside>
    </div>
  );
};

export default ForgotPassword2;
